import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useCallback, useContext, useMemo, useEffect, useState } from 'react';
import { Input } from '../Common/Input';
import { PayButton } from '../PaymentInputs/PayButton';
import { Email } from '../Common/Email';
import { useFormValidation } from '@/hooks/useFormValidation';
import { ValidationRules } from '@/utils/validation';
import type { ValidationRule } from '@/utils/validation';
import { useTranslate } from '@/hooks/useTranslate';
import type { NuveiField } from '@/types/nuvei';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { isEmailValid } from '@/utils/email';

// `mandatoryUserFields` arrive as raw Nuvei field keys. The ones the checkout knows how to ask for
// get a translated label; anything else keeps falling back to the key itself.
const MANDATORY_FIELD_LABELS: Record<string, string> = {
  email: 'emailAddress',
  firstName: 'firstName',
  lastName: 'lastName',
  phone: 'phoneNumber',
};

export const NuveiGeneralPayForm = () => {
  const {
    paymentMethodsSettings,
    otherMethodSelected,
    paymentMethodSelected,
    actions: { runPayment, setIsFormValid },
    customer,
  } = useContext(CheckoutContext);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const { t } = useTranslate();

  const paymentMethod = useMemo(() => {
    const selectedPaymentMethod =
      paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected;
    return paymentMethodsSettings.find((paymentMethod) => paymentMethod.paymentMethodName === selectedPaymentMethod)!;
  }, [paymentMethodSelected, otherMethodSelected, paymentMethodsSettings]);

  const getFieldLabel = useCallback(
    (field: string) => (MANDATORY_FIELD_LABELS[field] ? t(MANDATORY_FIELD_LABELS[field]) : field),
    [t]
  );

  const validationRules = useMemo(() => {
    const rules: Record<string, ValidationRule[]> = {};
    if (paymentMethod?.mandatoryUserFields) {
      Object.entries(paymentMethod.mandatoryUserFields).forEach(([field, isRequired]) => {
        // The email field is rendered as a separated component that has it's own validation rules outside of the useFormValidation hook
        if (field === 'email') {
          return;
        }
        const label = getFieldLabel(field);
        rules[field] = [
          ...(isRequired ? [ValidationRules.required(label, t('fieldErrors.incomplete', { field: label }))] : []),
          // Nuvei expects a bare international number, so reject separators and a leading '+'.
          // The pattern tolerates an empty value; `required` is what enforces presence.
          ...(field === 'phone' ? [ValidationRules.pattern(/^\d*$/, t('fieldErrors.telephoneNumber'))] : []),
        ];
      });
    }
    paymentMethod.additionalInputFields?.forEach((field) => {
      rules[field.key] = field.validation
        ? [ValidationRules.pattern(new RegExp(field.validation), t('fieldErrors.invalidFormat', { field: field.name }))]
        : [];
    });
    return rules;
  }, [paymentMethod?.mandatoryUserFields, paymentMethod.additionalInputFields, getFieldLabel, t]);

  const allFields = useMemo(() => {
    const mandatoryUserFields: NuveiField[] = Object.keys(paymentMethod.mandatoryUserFields || {}).map((field) => ({
      name: field,
      displayName: getFieldLabel(field),
    }));
    const additionalInputFields: NuveiField[] =
      paymentMethod.additionalInputFields?.map((field) => ({
        name: field.key,
        displayName: field.display_name || field.name,
      })) || [];
    return [...mandatoryUserFields, ...additionalInputFields];
  }, [paymentMethod.mandatoryUserFields, paymentMethod.additionalInputFields, getFieldLabel]);

  const fieldsWithoutEmail = useMemo(() => {
    if (!allFields.length) return [];
    return allFields.filter((field) => field.name !== 'email');
  }, [allFields]);

  const initialValues = useMemo(() => {
    // Publishers may already know the shopper's name, so seed the name inputs from it rather than
    // making them retype it. Everything the checkout cannot infer (phone) starts empty.
    const [firstName = '', ...remainingNameParts] = (customer?.name ?? '').trim().split(/\s+/);
    const prefilled: Record<string, string> = { firstName, lastName: remainingNameParts.join(' ') };

    return allFields.reduce(
      (values, field) => {
        return { ...values, [field.name]: prefilled[field.name] ?? '' };
      },
      {} as Record<string, string>
    );
  }, [allFields, customer?.name]);

  const { values, errors, touched, setValue, setFieldTouched, isValid } = useFormValidation(
    initialValues,
    validationRules
  );

  const isFormValid = useMemo(() => {
    return (fieldsWithoutEmail.length === 0 || isValid) && isEmailValid(customer?.email) && isZipCodeValid;
  }, [isValid, customer?.email, isZipCodeValid, fieldsWithoutEmail.length]);

  // Update global form validity
  useEffect(() => {
    setIsFormValid((isValid || fieldsWithoutEmail.length === 0) && isEmailValid(customer?.email));
  }, [isValid, customer?.email, setIsFormValid, fieldsWithoutEmail.length]);

  const handlePayment = useCallback(async () => {
    if (!isFormValid) {
      return;
    }
    await runPayment({ ...values }, undefined, ProviderEnum.NUVEI);
  }, [values, runPayment, isFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  const FieldsByMandatoryFields = useMemo(() => {
    if (!allFields.length) return null;
    return allFields.map((field) => {
      if (field.name === 'email') {
        return <Email key={field.name} />;
      }
      return (
        <Input
          key={field.name}
          type="text"
          className="w-full"
          placeholder={field.displayName}
          value={values[field.name] || ''}
          onChange={(e) => setValue(field.name, e.target.value)}
          onBlur={() => setFieldTouched(field.name)}
          error={touched[field.name] ? errors[field.name] : undefined}
        />
      );
    });
  }, [allFields, values, errors, touched, setValue, setFieldTouched, customer?.email]);

  return (
    <div className="flex flex-col gap-4">
      {FieldsByMandatoryFields}
      <InputTaxComponent onValidate={handleTaxValidationChange} />
      <PayButton onClick={handlePayment} isDisabled={!isFormValid} />
    </div>
  );
};
